const IMG = '/why/system';

export const WHY_SYSTEM_PAGE = {
  meta: {
    title: 'Construx — The Building System',
    description:
      'Construx delivers an engineered building envelope — digitally framed, interlocking, and assembled square the first time.',
  },
  sectionNav: [
    // { href: '#fabrication', label: 'Digital fabrication' },
    { href: '#why-now', label: 'Why now' },
    { href: '#shift', label: 'The shift' },
    { href: '#how', label: 'How it works' },
    { href: '#anatomy', label: 'Anatomy' },
    { href: '#perf', label: 'Performance' },
    { href: '#finish', label: 'Builds' },
  ],
  hero: {
    kicker: 'Construx · The building system',
    title: 'Construction, reimagined',
    titleAccent: '',
    lede:
      'Construx delivers an engineered building envelope — digitally framed, interlocking, and assembled square the first time. Not loose boards and guesswork. A system.',
    specs: [
      { value: '±2', unit: 'mm', label: 'Machine tolerance' },
      { value: 'Weeks', label: 'To weathertight shell' },
      { value: '1 system', label: 'Labeled · interlocking' },
    ],
    image: `${IMG}/hero-envelope.jpg`,
    imageAlt: 'Construx interior shell with precision-cut waffle wall panels and pre-cut rough openings',
    callouts: [
      'Precision-cut wall panel · CX-W01',
      'Pre-cut rough opening',
      'Self-aligning interlock',
    ],
  },
  digitalFabrication: {
    title: 'Construx',
    titleAccent: 'Digital Fabrication',
    steps: [
      {
        no: '1',
        title: 'Import',
        body:
          'Architectural plans are imported into our software where it is digitally converted to a Construx 3D model using CAD software',
        // image: `${IMG}/digital-fabrication/import.jpg`,
        alt: 'Architectural floor plan imported into Construx CAD software',
        mediaClass: 'digifab__media--cad',
      },
      {
        no: '2',
        title: 'Nesting',
        body:
          "Component parts are automatically laid out in a 'nest' using software and ready for CNC machining",
        // image: `${IMG}/digital-fabrication/nesting.png`,
        alt: 'CNC nest layout of Construx component parts on a machining sheet',
      },
      {
        no: '3',
        title: 'Build Guide',
        body:
          'Instructions to assemble parts into components are generated and auto-update when the CAD model changes',
        // image: `${IMG}/digital-fabrication/build-guide.png`,
        alt: 'Lacewood build guide with exploded component assembly diagrams',
      },
      {
        no: '4',
        title: 'Construction Guide',
        body:
          'Instructions to assemble parts into components are generated and auto-update when the CAD model changes',
        // image: `${IMG}/digital-fabrication/construction-guide.jpg`,
        alt: 'Construction guide elevation with highlighted wall assembly sequence',
      },
    ],
  },
  whyNow: {
    title: 'Why now?',
    pattern: '/why/why-now/pattern.png',
    lockup: '/why/why-now/construx-lockup.png',
    lockupAlt: 'Construx',
    columns: [
      {
        no: '1',
        lines: [
          { text: 'The United States Housing Market Is' },
          { text: 'SHORT 6.5M HOMES.', bold: true },
        ],
      },
      {
        no: '2',
        lines: [
          { text: 'THE HOUSING GAP WIDENS', bold: true },
          { text: 'As More Families Confront Fewer Homes.' },
        ],
      },
      {
        no: '3',
        lines: [
          { text: 'SKILLED LABOR SHORTAGES', bold: true },
          { text: 'Remain a Top Concern For the Construction Industry.' },
        ],
      },
      {
        no: '4',
        lines: [
          { text: "The United States' Housing Shortage" },
          { text: 'WILL NOT BE SOLVED', bold: true },
          { text: 'For Years To Come.' },
        ],
      },
    ],
    sources: [
      {
        logo: '/why/why-now/logos/cnn-business.svg',
        alt: 'CNN Business',
        date: 'March 8, 2023',
        href: 'https://www.cnn.com/2023/03/08/homes/housing-shortage/index.html',
      },
      {
        logo: '/why/why-now/logos/investopedia.svg',
        alt: 'Investopedia',
        date: 'March 8, 2023',
        href: 'https://www.investopedia.com/us-housing-outlook-7229463',
      },
      {
        logo: '/why/why-now/logos/business-insider.svg',
        alt: 'Business Insider',
        date: 'January 2024',
        href: 'https://www.businessinsider.com/housing-crisis-national-shortage-construction-workers-job-demand-2024-5',
      },
      {
        logo: '/why/why-now/logos/probuilder.svg',
        alt: 'ProBuilder',
        date: 'July 24, 2023',
        href: 'https://www.probuilder.com/business-management/market-data-trends/article/55273743/housing-shortage-could-last-another-7-years',
      },
    ],
  },
  proofBar: {
    tag: 'construction reimagined.',
    ticks: ['Digitally framed', 'Precision-cut', 'Interlocking assembly', 'Structurally superior'],
  },
  shift: {
    kicker: 'The shift',
    title: 'The frame comes together',
    titleAccent: 'differently now.',
    lede:
      'Traditional framing is cut and nailed board by board, on site, in the weather — every wall a fresh chance for error. Construx moves that work upstream into a model and a machine.',
    legacy: {
      tag: 'Conventional stick framing',
      badge: 'LEGACY',
      headline: 'Cut, measured, and nailed\nin your backyard.',
      items: [
        { bold: 'Hand tolerances.', text: 'Square and plumb depend on the crew and the day — drift of 10–25 mm is normal.' },
        { bold: 'Open to the elements.', text: 'Weeks of exposure while the frame goes up stick by stick.' },
        { bold: 'A pile of lumber.', text: 'Material shows up loose — measured, cut, and sorted on the job.' },
        { bold: 'Months of backyard chaos.', text: 'Variable quality, weather delays, and surprises baked into the schedule.' },
      ],
    },
    construx: {
      tag: 'The Construx system',
      badge: 'CX-METHOD',
      headline: 'Engineered to the millimeter, before you break ground.',
      items: [
        { bold: '±2 mm machine tolerance.', text: 'Every part cut from the model — square and flush by design.' },
        { bold: 'Fast to weathertight.', text: 'Interlocking components close the envelope in weeks, not months.' },
        { bold: 'One labeled system.', text: 'Components arrive numbered and interlocking — assembled on .' },
        { bold: 'Structurally superior, every time.', text: 'A repeatable result — not a one-off built from scratch.' },
      ],
    },
  },
  pipeline: {
    kicker: 'The pipeline · model to envelope',
    title: 'Four stages.',
    titleAccent: 'Zero guesswork.',
    lede:
      "The same digital model drives everything downstream — so what's engineered on screen is exactly what gets machined, shipped, and snapped together on site.",
    steps: [
      {
        no: '01',
        phase: 'CX · Design',
        title: 'Digital framing',
        body: 'Your building is engineered as a digital model — every wall, opening, and connection resolved to the millimeter before anything is cut.',
        image: `${IMG}/digital-framing.jpg`,
      },
      {
        no: '02',
        phase: 'CX · Machine',
        title: 'Precision-cut system components',
        body: 'Components are machine-cut straight from the model into a labeled, interlocking system — cut-optimized, repeatable, and waste-minimized.',
        image: `${IMG}/cnc-kit.jpg`,
      },
      {
        no: '03',
        phase: 'CX · Assemble',
        title: 'Interlocking envelope (front)',
        body: 'The system goes together square, flush, and self-aligning — closing a weathertight, structurally superior shell in a fraction of framing time.',
        image: `${IMG}/envelope.jpg`,
      },
      {
        no: '04',
        phase: 'CX · Finish',
        title: 'Finished to spec\n(back)',
        body: 'Premium finishes, structural glass, and handcrafted detail land on a perfect substrate — a high-performance space, every single time.',
        image: `${IMG}/finished-to-spec.jpg`,
        alt: 'Finished coastal home with lap siding, balcony, and waterfront setting',
        // href: 'https://kinnakeetvillas.com/property/unit-10/',
      },
    ],
  },
  anatomy: {
    kicker: 'Anatomy of the system',
    title: 'Every part,',
    titleAccent: 'accounted for.',
    lede:
      'Hover a component to locate it on the envelope. Nothing on the job site is improvised — each piece is engineered, numbered, and placed.',
    image: `${IMG}/anatomy-envelope.jpg`,
    imageAlt: 'Construx interior envelope with waffle wall panels, vaulted roof deck, pre-cut window openings, and floor system components',
    parts: [
      { id: 'roof', code: 'CX-R01', title: 'Roof System', body: 'Engineered roof system component, machined and integrated into the envelope — not framed separately on site.', hot: { left: '24%', top: '6%', width: '50%', height: '26%' }, label: { left: '24%', top: '6%' }, labelText: 'CX-R01 · ROOF SYSTEM' },
      { id: 'wall', code: 'CX-W01', title: 'Wall System', body: 'Interlocking wall section cut from the model, with sheathing and structure as one repeatable unit.', hot: { left: '17%', top: '32%', width: '13%', height: '46%' }, label: { left: '4%', top: '26%' }, labelText: 'CX-W01 · WALL SYSTEM' },
      { id: 'window', code: 'CX-O01', title: 'Pre-cut rough opening', body: 'Window and door openings are machined to spec — no field cutting, no drift, glazing-ready.', hot: { left: '22%', top: '28%', width: '18%', height: '28%' }, label: { left: '18%', top: '30%' }, labelText: 'CX-O01 · ROUGH OPENING' },
      { id: 'corner', code: 'CX-J01', title: 'Self-aligning interlock', body: 'Corner and panel joints that register themselves — the system guides the crew to square.', hot: { left: '18%', top: '62%', width: '18%', height: '14%' }, label: { left: '8%', top: '66%' }, labelText: 'CX-J01 · INTERLOCK' },
      { id: 'base', code: 'CX-F01', title: 'Floor System', body: 'Precision-cut engineered panel floor deck, labeled and load-rated — the level datum the whole system builds from.', hot: { left: '12%', top: '80%', width: '72%', height: '12%' }, label: { left: '12%', top: '84%' }, labelText: 'CX-F01 · FLOOR SYSTEM' },
    ],
  },
  performance: {
    kicker: 'Performance',
    title: 'The spec sheet.',
    lede: 'What an engineered system delivers, measured against framing the same building by hand.',
    note: '// Figures illustrate the Construx volumetric system method. Actual specifications vary by project and jurisdiction.',
    rows: [
      { metric: 'Framing tolerance', sub: 'Deviation from true', conventional: '±10–25 mm', construx: '±2 mm' },
      { metric: 'Time to weathertight shell', sub: 'From start of framing', conventional: '8–12 weeks', construx: '~2–3 weeks' },
      { metric: 'Material delivery', sub: 'What arrives on site', conventional: 'Loose lumber', construx: '1 labeled system' },
      { metric: 'Squareness & fit', sub: 'Wall and corner accuracy', conventional: 'Crew-dependent', construx: 'Machine-true' },
      { metric: 'Material waste', sub: 'Offcut and rework', conventional: 'High', construx: 'Often none' },
      { metric: 'Repeatability', sub: 'Result build to build', conventional: 'One-off', construx: 'High, every time' },
    ],
  },
  finish: {
    kicker: 'From system to finish',
    title: 'A precise frame',
    titleAccent: 'disappears',
    titleEnd: 'into a flawless finish.',
    lede:
      'The engineering is invisible in the end — which is the point. Premium materials land on a perfectly square substrate, so the doors close right and the lines stay true.',
    leadImage: `${IMG}/guest-suite.png`,
    leadAlt: 'A finished modern backyard guest suite at dusk with structural glass and warm interior light',
    grid: [
      { src: `${IMG}/backyard-office.png`, alt: 'Backyard office at dusk', caption: 'Backyard office · CX build' },
      { src: `${IMG}/flex-space.png`, alt: 'Backyard flex space set up as a gym', caption: 'Flex space · CX build' },
      { src: '/1000005565.jpg', alt: 'Construx interior framing', caption: 'Interior frame · CX-W01' },
      { src: `${IMG}/finished-build.png`, alt: 'Finished home addition at dusk', caption: 'Home addition · CX build' },
    ],
  },
  fieldNotes: {
    kicker: 'Field notes · Austin, TX',
    title: 'Where the system',
    titleAccent: 'saves time.',
    source: '// Contractor field notes · ADU build · Austin, TX',
    rows: [
      {
        id: 'plan',
        phase: 'Planning & Permits',
        trade: 'GC · Engineer',
        conventional: { label: 'Weeks of revisions and back-and-forth', pct: 88 },
        construx: { label: '2 days · approved first submission', pct: 16 },
        note: '"We submitted. The city approved it right away. There were no revisions."',
      },
      {
        id: 'frame',
        phase: 'Framing',
        trade: 'Framing crew',
        conventional: { label: 'Unknown finish · weeks on site', pct: 100 },
        construx: { label: '3 days · predictable start and end', pct: 20 },
        note: '"You know exactly when it\'s going to start and end."',
      },
      {
        id: 'sheath',
        phase: 'Sheathing',
        trade: 'Sheathing crew',
        conventional: { label: 'Separate crew, materials order, scheduling', pct: 72 },
        construx: { label: 'Eliminated — arrives with the frame', pct: 0, eliminated: true },
        note: '"The sheathing part was solved."',
      },
      {
        id: 'mep',
        phase: 'Rough-in MEP',
        trade: 'Plumber · Electrician',
        conventional: { label: 'Coordinate around framing as it progresses', pct: 78 },
        construx: { label: 'Access pre-opened · show up and work', pct: 38 },
        note: '"The cavities gave us easy access to do the rough-in."',
      },
      {
        id: 'rework',
        phase: 'Rework',
        trade: 'All trades',
        conventional: { label: 'Field mistakes built into every job', pct: 62 },
        construx: { label: 'Zero — precision-cut, nothing to correct', pct: 0, eliminated: true },
        note: '"There was no need for any rework. We built it, put it together, and it was done."',
      },
    ],
    quote: '"Faster, stronger, easy to build, and easy to work with — from the GC perspective, those are strong points."',
    quoteAuthor: 'Austin Contractor',
  },
  testimonials: {
    kicker: 'Customer feedback',
    title: 'What customers',
    titleAccent: 'are saying.',
    items: [
      {
        quote:
          'Having the unit built with precision-cut parts eliminated those mistakes, and everything was pretty much accurate. It was three days for us to work on that — you know exactly when it’s going to start and end.',
        author: 'Hagai',
        partnerName: 'Austin Remodel Pro',
        company: 'Austin, TX',
        href: '/partners/austin-remodel-pro',
        featured: true,
        featuredLabel: 'Featured builder',
      },
      {
        quote:
          "The Construx Building System's modular design allowed for efficient construction, minimizing waste and maximizing energy efficiency. We highly recommend Construx Building System for anyone seeking a sustainable, high-quality, and aesthetically pleasing home.",
        author: 'Homeowner',
        company: 'Charleston, SC',
      },
      {
        quote:
          'I am very pleased with how easy it is assemble! I will definitely give it 5 stars not only because it is great, but also because we need a solution like this for our growing business.',
        author: 'Builder',
        company: 'Austin, TX',
      },
      {
        quote:
          'The Construx system is perfect for our disaster recovery projects. We just had a crew of volunteers that had never built a home assemble a Construx 3BR house in just 4 days!',
        author: 'Charitable Foundation',
        company: 'Raleigh, NC',
      },
    ],
  },
  systemsGallery: {
    items: [
      {
        name: 'Floor System',
        src: '/why/floor-system.jpg',
        alt: 'Construx precision-cut engineered panel floor deck — on a square, labeled platform',
        objectPosition: '50% 65%',
      },
      {
        name: 'Super Ring System',
        src: '/why/super-ring-system.jpg',
        alt: 'Construx super ring tying walls, floor, and roof together — aerial view of panelized frame assembly',
      },
      {
        name: 'Stair System',
        src: '/why/stairs.jpg',
        alt: 'Construx integrated stair components',
      },
      {
        name: 'Roof System',
        src: '/why/roof-system.jpg',
        alt: 'Construx precision-engineered panel roof system component — coffered deck panels on an exterior balcony overhang',
        objectPosition: '50% 35%',
      },
    ],
  },
  liveProject: {
    kicker: 'Builds · Guest house · ADU',
    photos: [
      { src: '/why/live-project/lp-01.webp', alt: 'Construx system assembly on site' },
      { src: '/why/live-project/lp-02.webp', alt: 'Construx precision-cut frame' },
      { src: '/why/live-project/lp-03.webp', alt: 'Construx build in progress' },
      { src: '/why/live-project/lp-04.webp', alt: 'Construx system components on site' },
      { src: '/why/live-project/lp-05.webp', alt: 'Construx frame detail' },
      { src: '/why/live-project/lp-06.webp', alt: 'Construx system in the field' },
      { src: '/why/live-project/lp-07.webp', alt: 'Construx frame assembly' },
      { src: '/why/live-project/lp-09.webp', alt: 'Construx build exterior' },
      { src: '/why/live-project/lp-10.webp', alt: 'Construx system detail' },
      { src: '/why/live-project/lp-11.webp', alt: 'Construx finished build' },
    ],
  },
  cta: {
    kicker: 'Build with the system',
    title: 'Put Construx',
    titleAccent: 'to work.',
    cards: [
      {
        tag: 'Homeowners',
        title: 'Want to build in your backyard?',
        body: 'Office, guest suite, studio, or flex space — digitally framed by Construx and built by a vetted local partner. Get matched and quoted in about 30 seconds.',
        href: '/backyard-austin',
        label: 'Get started',
      },
      {
        tag: 'Builders & contractors',
        title: 'Build with Construx in your market.',
        body: "Get certified on the system, take qualified leads, and deliver a faster, squarer build. We're growing the partner network across the country.",
        href: '/partners',
        label: 'Become a partner',
        featured: true,
      },
    ],
  },
};
